import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { DealActions, DealDetail, DefaultCollateralIdentifierType, EntityTypeEnum, NegotiationFieldMeta, SubscriptionIdentifier } from 'api'
import { CenterPanel } from 'view/routes/routesLayout'
import { DataGrid, filteredColumnDefsBuilder, GridAutoSizing, GridId } from 'view/components/datagrid'
import {
  getMidTradeEventsGridColumnDefs,
  MidTradeEventFieldNames,
  MidTradeEventsGridColumnId,
  midTradeEventsGridOptions
} from '../DataGrid'
import { MidTradeEventsHeroPanel } from './MidTradeEventsHeroPanel'
import {
  MID_TRADE_VIEW_HISTORICAL,
  MID_TRADE_VIEW_INCOMING,
  MID_TRADE_VIEW_INITIATED,
  sortedMidTradeEventsDetailsViewSelector
} from 'selectors/midTradeEventsSelectors'
import { AdminOnlyColumnIds } from 'view/routes/ColumnNames'
import { canServicingUpdateAttributes, canServicingUpdateRegulatoryAttributes } from 'view/routes/deals/components/utils'
import { primitive, useAppDispatch, useParametricSelector } from 'utils'
import { MidTradeEventDropzoneProvider } from 'view/shared/midTradeEventUpload'
import { MidTradeEventsGridPlaceholder } from '../DataGrid/MidTradeEventsGridPlaceholder'
import { canServiceTradeSelector } from 'selectors/permissionSelectors'
import { defaultCollateralIdentifierTypeSelector } from 'selectors/userPreferenceSelectors'
import { GridApi } from 'ag-grid-community'
import { OrganisationFilterSwitch } from 'view/components/datagrid/toolbar/OrganisationFilterSwitch'
import { startConsiderationOverrideEnabledSelector, isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { generateExportFileName } from 'view/components/datagrid/gridUtils/utils'
import { useIsAdmin, useIsCurrentOrganisationBuySide, useSubscription, useUserPreference } from 'view/hooks'
import { MidTradeEventsViewContext } from './MidTradeEventsViewContext'
import { RelatedPanel } from 'view/routes/deals/components/DealsScreenBase/RelatedPanel'
import { relatedMidTradeEventsSelector } from 'selectors/dealSelectors'
import { RelatedDealsThunks } from 'thunks'

const NegotiationFieldLookups = {
  [MidTradeEventFieldNames.NOTIONAL_MODIFIED]: NegotiationFieldMeta.NOTIONAL,
  [MidTradeEventFieldNames.RATE_MODIFIED]: NegotiationFieldMeta.RATE,
  [MidTradeEventFieldNames.HAIRCUT_MODIFIED]: NegotiationFieldMeta.HAIRCUT,
  [MidTradeEventFieldNames.HAIRCUT_TYPE_MODIFIED]: NegotiationFieldMeta.HAIRCUT_TYPE,
  [MidTradeEventFieldNames.START_DATE_MODIFIED]: NegotiationFieldMeta.START_DATE,
  [MidTradeEventFieldNames.RERATE_DATE_MODIFIED]: NegotiationFieldMeta.RERATE_DATE,
  [MidTradeEventFieldNames.START_CONSIDERATION_MODIFIED]: NegotiationFieldMeta.START_CONSIDERATION,
  [MidTradeEventFieldNames.BOND_PRICE_MODIFIED]: NegotiationFieldMeta.BOND_PRICE,
  [MidTradeEventFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_MODIFIED]: NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER,
  [MidTradeEventFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_MODIFIED]: NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER,
  [MidTradeEventFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED]: NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER,
  [MidTradeEventFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED]: NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER,
  [MidTradeEventFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED]: NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE,
  [MidTradeEventFieldNames.SECONDARY_BASKET_ID_MODIFIED]: NegotiationFieldMeta.SECONDARY_BASKET_ID,
  [MidTradeEventFieldNames.FEE_MODIFIED]: NegotiationFieldMeta.FEE
}

function getDecisionMakerValue(dealDetail: DealDetail, idmField: boolean, field: string, value: string) {
  const idm = dealDetail.ownOrder
    ? dealDetail.servicingSideDetailsMeta?.INITIATOR_INVESTMENT_DECISION_MAKER
    : dealDetail.servicingSideDetailsMeta?.RESPONDER_INVESTMENT_DECISION_MAKER
  const edm = dealDetail.ownOrder
    ? dealDetail.servicingSideDetailsMeta?.INITIATOR_EXECUTION_DECISION_MAKER
    : dealDetail.servicingSideDetailsMeta?.RESPONDER_EXECUTION_DECISION_MAKER
  if (idmField) {
    return field.includes('INVESTMENT') ? value : idm?.id ?? null
  } else {
    return field.includes('EXECUTION') ? value : edm?.id ?? null
  }
}

export const MidTradeEventsScreen = ({
  view
}: {
  view?: typeof MID_TRADE_VIEW_HISTORICAL | typeof MID_TRADE_VIEW_INITIATED | typeof MID_TRADE_VIEW_INCOMING
}) => {
  const dispatch = useAppDispatch()
  const isAdminUser = useIsAdmin()
  const relatedDealDetails = useParametricSelector(relatedMidTradeEventsSelector, view)
  const midTradeEvents = useParametricSelector(sortedMidTradeEventsDetailsViewSelector, view)
  const subscriptionDetail = useSubscription(EntityTypeEnum.DEAL_HISTORY, SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS)
  const canService = useSelector(canServiceTradeSelector)
  const defaultCollateralIdentifierType = useSelector(defaultCollateralIdentifierTypeSelector)
  const startConsiderationOverrideEnabled = useSelector(startConsiderationOverrideEnabledSelector)
  const stockBorrowingLendingEnabled = useSelector(isStockBorrowingLendingEnabledSelector)
  const userPreference = useUserPreference()
  const isBuySideOrganisation = useIsCurrentOrganisationBuySide()

  const columnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(getMidTradeEventsGridColumnDefs(isAdminUser))
        .withoutIf(!isAdminUser, Object.values(AdminOnlyColumnIds))
        .withoutIf(!startConsiderationOverrideEnabled, [
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_START_CONSIDERATION_OVERRIDE,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_START_CONSIDERATION_OVERRIDE
        ])
        .withoutIf(!stockBorrowingLendingEnabled, [
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_SECONDARY_COLLATERAL_NAME,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_SECONDARY_COLLATERAL_NAME,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_FEE,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_FEE,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_STOCK_BORROWING_LENDING_TYPE,
          MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_STOCK_BORROWING_LENDING_TYPE
        ])
        .withoutIf(!isBuySideOrganisation, [MidTradeEventsGridColumnId.SUBMITTING_USER])
        .build(),
    [isAdminUser, startConsiderationOverrideEnabled, isBuySideOrganisation, stockBorrowingLendingEnabled]
  )

  const shouldStartEdit = (dealDetail: DealDetail | null | undefined) =>
    !!dealDetail && (canServicingUpdateAttributes(dealDetail) || canServicingUpdateRegulatoryAttributes(dealDetail))

  const handleCellValueUpdated = (dealId: string, dealDetail: DealDetail, field: string, value: primitive, rawValue?: primitive) => {
    if (
      [
        MidTradeEventFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_MODIFIED,
        MidTradeEventFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_MODIFIED,
        MidTradeEventFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED,
        MidTradeEventFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED
      ].includes(field)
    ) {
      dispatch(
        DealActions.servicingUpdateRegulatoryAttributes(
          dealId,
          getDecisionMakerValue(dealDetail, true, field, value as string),
          getDecisionMakerValue(dealDetail, false, field, value as string),
          false
        )
      )
    } else if (field === MidTradeEventFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED) {
      dispatch(
        DealActions.servicingUpdateAttributes({
          dealId,
          field: NegotiationFieldLookups[field],
          updatedValue: value,
          updatedRawValue: rawValue,
          decisionMakerOrientedByNegotiationSide: true,
          meta: {
            haircutSign: userPreference.haircutSign
          },
          revertFirst: false
        })
      )
      dispatch(
        DealActions.servicingUpdateAttributes({
          dealId,
          field: NegotiationFieldLookups[MidTradeEventFieldNames.START_CONSIDERATION_MODIFIED],
          updatedValue: null,
          updatedRawValue: '',
          decisionMakerOrientedByNegotiationSide: true,
          meta: {
            haircutSign: userPreference.haircutSign
          },
          revertFirst: false
        })
      )
    } else {
      dispatch(
        DealActions.servicingUpdateAttributes({
          dealId,
          field: NegotiationFieldLookups[field],
          updatedValue: value,
          updatedRawValue: rawValue,
          decisionMakerOrientedByNegotiationSide: true,
          meta: {
            haircutSign: userPreference.haircutSign
          },
          revertFirst: false
        })
      )
    }
  }

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    gridApi.setColumnsVisible(
      [MidTradeEventsGridColumnId.MID_TRADE_EVENT_COLLATERAL_ISIN_OR_BUI, MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_ISIN],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
    )

    gridApi.setColumnsVisible(
      [MidTradeEventsGridColumnId.MID_TRADE_EVENT_COLLATERAL_CUSIP, MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_COLLATERAL_CUSIP],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN
    )
  }

  const handleRelatedOnClose = () => {
    if (view === MID_TRADE_VIEW_INITIATED) {
      dispatch(RelatedDealsThunks.updateMteInitiatedRelatedDealsSubscription(null))
    } else if (view === MID_TRADE_VIEW_INCOMING) {
      dispatch(RelatedDealsThunks.updateMteIncomingRelatedDealsSubscription(null))
    }
  }

  const onPageChanged = (pageNumber: number) => {
    if (subscriptionDetail) {
      dispatch(DealActions.pageHistorySubscribe(SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS, subscriptionDetail.filters, pageNumber))
    }
  }

  return (
    <MidTradeEventsViewContext.Provider value={view}>
      <MidTradeEventDropzoneProvider>
        <CenterPanel>
          <MidTradeEventsHeroPanel />
          <DataGrid
            gridColumnAutoSizing={GridAutoSizing.IGNORE_HEADERS}
            gridId={GridId.MidTradeEventsGrid}
            gridClass="upload-grid"
            gridOptions={midTradeEventsGridOptions}
            columnDefs={columnDefs}
            rowData={midTradeEvents}
            customToolbarSwitches={[OrganisationFilterSwitch]}
            canEditRow={shouldStartEdit}
            onCellValueUpdated={handleCellValueUpdated}
            placeholder={canService && view !== MID_TRADE_VIEW_HISTORICAL ? () => <MidTradeEventsGridPlaceholder /> : undefined}
            onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
            subscriptionDetail={view === MID_TRADE_VIEW_HISTORICAL ? subscriptionDetail : undefined}
            onPageChanged={view === MID_TRADE_VIEW_HISTORICAL ? onPageChanged : undefined}
            exportFileName={generateExportFileName(view ? `MTE_${view}` : 'MTE_All')}
          />
          <RelatedPanel dealDetails={relatedDealDetails} parentGridId={GridId.MidTradeEventsGrid} onClose={handleRelatedOnClose} />
        </CenterPanel>
      </MidTradeEventDropzoneProvider>
    </MidTradeEventsViewContext.Provider>
  )
}
